/**
 * OcrReviewQueue — /ocr/review  (redesigned UX hardening sprint)
 *
 * Doc-controller workbench for OCR pages flagged for human review. Left: a queue
 * of pages with confidence meters (sorted lowest-confidence first). Right: a
 * review pane that shows the extracted text with low-confidence terms
 * highlighted, an editable correction surface, and accept / correct actions.
 *
 * Regulatory note (OCR-004): the authoritative artifact is the locked PDF in
 * document-svc; the OCR text is advisory. The compare view keeps both visible.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero, StatCard, Stagger, Skeleton, EmptyState, Button } from '@edms/design-system';
import {
  useOcrReviewQueue,
  useApplyOcrCorrection,
  useDocumentsLite,
  useQueueOcrRun,
  type OcrReviewItem,
} from '../../api/hooks';
import styles from './OcrReviewQueue.module.css';

function band(score: number): 'low' | 'med' | 'high' {
  return score < 0.7 ? 'low' : score < 0.85 ? 'med' : 'high';
}

/** Split text into segments, marking flagged substrings for highlight. */
function highlight(text: string, terms: string[]): Array<{ t: string; flag: boolean }> {
  if (!terms.length) return [{ t: text, flag: false }];
  const escaped = terms.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).filter(Boolean);
  if (!escaped.length) return [{ t: text, flag: false }];
  const re = new RegExp(`(${escaped.join('|')})`, 'g');
  return text.split(re).filter((s) => s !== '').map((s) => ({ t: s, flag: terms.includes(s) }));
}

export function OcrReviewQueue() {
  const { t, i18n } = useTranslation();
  const queueQ = useOcrReviewQueue();
  const correction = useApplyOcrCorrection();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [showQueue, setShowQueue] = useState(false);

  const items = useMemo(
    () => [...(queueQ.data?.items ?? [])].sort((a, b) => a.confidence_score - b.confidence_score),
    [queueQ.data],
  );
  const selected = items.find((i) => i.page_id === selectedId) ?? null;

  const stats = useMemo(() => {
    if (items.length === 0) return { count: 0, avg: 0, min: 0, langs: 0 };
    const avg = items.reduce((a, b) => a + b.confidence_score, 0) / items.length;
    const min = Math.min(...items.map((i) => i.confidence_score));
    const langs = new Set(items.map((i) => i.language_detected)).size;
    return { count: items.length, avg, min, langs };
  }, [items]);

  function pick(item: OcrReviewItem) {
    setSelectedId(item.page_id);
    setDraft(item.text_content);
    setEditing(false);
    setNote(null);
  }
  async function save() {
    if (!selected) return;
    try {
      await correction.mutateAsync({ pageId: selected.page_id, correctedText: draft });
      setNote(t('ocr.review.saved', { defaultValue: 'Correction saved and audited.' }));
      setEditing(false);
      queueQ.refetch();
    } catch {
      setNote(t('ocr.review.saveFailed', { defaultValue: 'Could not save the correction.' }));
    }
  }
  async function acceptAsIs() {
    if (!selected) return;
    try {
      await correction.mutateAsync({ pageId: selected.page_id, correctedText: selected.text_content });
      setNote(t('ocr.review.accepted', { defaultValue: 'Page accepted as extracted.' }));
      queueQ.refetch();
    } catch {
      setNote(t('ocr.review.saveFailed', { defaultValue: 'Could not save.' }));
    }
  }

  const textDir = selected?.language_detected === 'ar' ? 'rtl' : 'ltr';
  const pct = (s: number) => `${Math.round(s * 100)}%`;

  return (
    <div className={styles.screen}>
      <PageHero
        eyebrow={t('ocr.review.eyebrow', { defaultValue: 'Documents' })}
        title={t('ocr.review.title', { defaultValue: 'OCR review queue' })}
        subtitle={t('ocr.review.subtitle', { defaultValue: 'Low-confidence scanned pages flagged for human review — correct the extracted text against the source.' })}
        actions={
          <Button variant="primary" leadingIcon="+" onClick={() => setShowQueue(true)}>
            {t('ocr.review.actions.queue', { defaultValue: 'Queue scanned page' })}
          </Button>
        }
      />

      {showQueue && (
        <QueueScannedPageDialog
          onClose={() => setShowQueue(false)}
          onQueued={(pageId) => {
            setShowQueue(false);
            queueQ.refetch();
            setSelectedId(pageId);
          }}
        />
      )}

      {queueQ.isLoading && (
        <>
          <div className={styles.kpiStrip} aria-hidden="true">{[0,1,2,3].map((i)=><Skeleton key={i} height={104} radius={14} />)}</div>
          <Skeleton height={360} radius={14} />
        </>
      )}
      {queueQ.isError && <p className={styles.error} role="alert">{t('ocr.review.errors.load_failed', { defaultValue: 'Could not load the queue.' })}</p>}

      {queueQ.data && (
        <>
          <Stagger className={styles.kpiStrip} step={50}>
            <StatCard label={t('ocr.review.kpi.pending', { defaultValue: 'Pages pending' })} value={stats.count} accent="var(--spectro-purple, #9333EA)" />
            <StatCard label={t('ocr.review.kpi.avg', { defaultValue: 'Avg confidence' })} value={Math.round(stats.avg * 100)} suffix="%" accent="var(--spectro-blue, #0065FF)" />
            <StatCard label={t('ocr.review.kpi.min', { defaultValue: 'Lowest confidence' })} value={Math.round(stats.min * 100)} suffix="%" accent={stats.min < 0.7 ? 'var(--color-status-error, #DC2626)' : 'var(--color-status-warning, #D97706)'} />
            <StatCard label={t('ocr.review.kpi.langs', { defaultValue: 'Languages' })} value={stats.langs} accent="var(--spectro-violet, #3F3DFF)" />
          </Stagger>

          {items.length === 0 ? (
            <EmptyState icon="✓" title={t('ocr.review.emptyTitle', { defaultValue: 'Queue is clear' })} description={t('ocr.review.empty', { defaultValue: 'No scanned pages are awaiting review. New low-confidence pages will appear here automatically.' })} />
          ) : (
            <div className={styles.layout}>
              <aside className={styles.queue} aria-label={t('ocr.review.queue_aria', { defaultValue: 'Review queue' })}>
                {items.map((item) => {
                  const b = band(item.confidence_score);
                  const on = item.page_id === selectedId;
                  return (
                    <button key={item.page_id} type="button" className={`${styles.qCard} ${on ? styles.qOn : ''}`} onClick={() => pick(item)} aria-current={on ? 'true' : undefined}>
                      <div className={styles.qTop}>
                        <strong className={styles.qNum}>{item.doc_number}</strong>
                        <span className={`${styles.langChip} ${styles[`lang_${item.language_detected.replace('+','_')}`] ?? ''}`}>{item.language_detected.toUpperCase()}</span>
                      </div>
                      <span className={styles.qTitle}>{item.doc_title ?? ''}</span>
                      <span className={styles.qMeta}>{t('ocr.review.row.page', { defaultValue: 'Page {{page}}', page: nf.format(item.page_number) })}{item.page_count ? ` / ${item.page_count}` : ''}{item.document_state ? ` · ${item.document_state.replace(/_/g,' ').toLowerCase()}` : ''}</span>
                      <div className={styles.meter} title={pct(item.confidence_score)}>
                        <div className={`${styles.meterFill} ${styles[`m_${b}`]}`} style={{ width: pct(item.confidence_score) }} />
                      </div>
                      <span className={`${styles.confLabel} ${styles[`c_${b}`]}`}>{pct(item.confidence_score)} {t('ocr.review.confidence', { defaultValue: 'confidence' })}</span>
                    </button>
                  );
                })}
              </aside>

              <section className={styles.detail} aria-label={t('ocr.review.detail_aria', { defaultValue: 'Page review' })}>
                {!selected ? (
                  <EmptyState icon="🔍" title={t('ocr.review.pickTitle', { defaultValue: 'Select a page to review' })} description={t('ocr.review.placeholder', { defaultValue: 'Pick a flagged page from the queue to compare the source against the extracted text.' })} />
                ) : (
                  <>
                    <header className={styles.detailHead}>
                      <div>
                        <h2 className={styles.detailTitle}>{selected.doc_number} · {t('ocr.review.detail.page', { defaultValue: 'Page {{page}}', page: nf.format(selected.page_number) })}</h2>
                        <span className={styles.detailSub}>{selected.doc_title}</span>
                      </div>
                      <span className={`${styles.confBadge} ${styles[`c_${band(selected.confidence_score)}`]}`}>{pct(selected.confidence_score)}</span>
                    </header>

                    {note && <div role="status" className={styles.note}>{note}</div>}

                    <div className={styles.compare}>
                      <div className={styles.pane}>
                        <h3 className={styles.paneTitle}>{t('ocr.review.pane.pdf', { defaultValue: 'Scanned source · facsimile' })}</h3>
                        <div className={styles.facsimile}>
                          <div className={styles.scanHead}>
                            <span className={styles.scanMark}>{t('ocr.review.scanned', { defaultValue: 'SCANNED PAGE' })}</span>
                            <span className={styles.scanRef}>{selected.doc_number} · p.{selected.page_number}{selected.page_count ? `/${selected.page_count}` : ''}</span>
                          </div>
                          <div className={styles.scanBody} dir={textDir} lang={selected.language_detected === 'ar' ? 'ar' : 'en'}>
                            {selected.text_content}
                          </div>
                          <p className={styles.pageFallback}>{t('ocr.review.pdfFallback', { defaultValue: 'Facsimile of the scanned page. The authoritative locked PDF is held in document-svc; the extracted text (right) is advisory until corrected + accepted.' })}</p>
                        </div>
                      </div>

                      <div className={styles.pane}>
                        <div className={styles.paneTitleRow}>
                          <h3 className={styles.paneTitle}>{t('ocr.review.pane.text', { defaultValue: 'Extracted text · advisory' })}</h3>
                          {!editing && (
                            <button type="button" className={styles.editLink} onClick={() => setEditing(true)}>{t('ocr.review.edit', { defaultValue: 'Edit' })}</button>
                          )}
                        </div>
                        {editing ? (
                          <textarea
                            className={styles.textArea}
                            dir={textDir}
                            lang={selected.language_detected === 'ar' ? 'ar' : 'en'}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            rows={14}
                            aria-label={t('ocr.review.pane.text_aria', { defaultValue: 'Correctable extracted text' })}
                          />
                        ) : (
                          <p className={styles.extracted} dir={textDir}>
                            {highlight(selected.text_content, selected.flagged_terms ?? []).map((seg, i) =>
                              seg.flag ? <mark key={i} className={styles.flag}>{seg.t}</mark> : <span key={i}>{seg.t}</span>,
                            )}
                          </p>
                        )}
                        {(selected.flagged_terms?.length ?? 0) > 0 && !editing && (
                          <p className={styles.flagNote}>{t('ocr.review.flagNote', { defaultValue: '{{n}} low-confidence term(s) highlighted — edit to correct.', n: selected.flagged_terms!.length })}</p>
                        )}
                      </div>
                    </div>

                    <footer className={styles.actions}>
                      <Button variant="secondary" onClick={() => setSelectedId(null)} disabled={correction.isPending}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                      {editing ? (
                        <Button variant="primary" onClick={() => void save()} isLoading={correction.isPending} disabled={draft === selected.text_content}>{t('ocr.review.actions.save_correction', { defaultValue: 'Save correction' })}</Button>
                      ) : (
                        <Button variant="primary" onClick={() => void acceptAsIs()} isLoading={correction.isPending}>{t('ocr.review.actions.accept', { defaultValue: 'Accept as extracted' })}</Button>
                      )}
                    </footer>
                  </>
                )}
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * QueueScannedPageDialog — ingest a scanned page through the (simulated) OCR
 * engine. Posts a real ocr.runs + ocr.pages row; pages with OCR artifacts land
 * below the confidence threshold and appear flagged in the queue above. (OCR-FN.)
 */
function QueueScannedPageDialog({
  onClose,
  onQueued,
}: {
  onClose: () => void;
  onQueued: (pageId: string) => void;
}) {
  const { t } = useTranslation();
  const docsQ = useDocumentsLite();
  const run = useQueueOcrRun();
  const [documentId, setDocumentId] = useState('');
  const [pageText, setPageText] = useState('');
  const [language, setLanguage] = useState('en');
  const [error, setError] = useState<string | null>(null);

  const docs = docsQ.data?.items ?? [];

  async function submit() {
    if (!documentId || !pageText.trim()) {
      setError(
        t('ocr.queue.missing', {
          defaultValue: 'Pick a document and paste the scanned page text.',
        }),
      );
      return;
    }
    setError(null);
    try {
      const res = await run.mutateAsync({ documentId, pageText: pageText.trim(), language });
      onQueued(res.page_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('ocr.queue.failed', { defaultValue: 'OCR run failed.' }));
    }
  }

  return (
    <div className={styles.ocrDialogShade} role="dialog" aria-modal="true">
      <div className={styles.ocrDialogBox}>
        <h2 className={styles.ocrDialogTitle}>
          {t('ocr.queue.title', { defaultValue: 'Queue a scanned page for OCR' })}
        </h2>
        <p className={styles.ocrDialogSub}>
          {t('ocr.queue.subtitle', {
            defaultValue:
              'Attach a scanned page to a controlled document. The OCR engine extracts the text and flags low-confidence pages for review here.',
          })}
        </p>

        <div className={styles.ocrField}>
          <label htmlFor="ocr-doc">
            {t('ocr.queue.document', { defaultValue: 'Document' })}
          </label>
          <select id="ocr-doc" value={documentId} onChange={(e) => setDocumentId(e.target.value)}>
            <option value="">
              {docsQ.isLoading
                ? t('common.loading', { defaultValue: 'Loading…' })
                : t('ocr.queue.pickDoc', { defaultValue: 'Select a document…' })}
            </option>
            {docs.map((d) => (
              <option key={d.document_id} value={d.document_id}>
                {d.doc_number} — {d.title}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.ocrField}>
          <label htmlFor="ocr-lang">
            {t('ocr.queue.language', { defaultValue: 'Language' })}
          </label>
          <select id="ocr-lang" value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="en">English</option>
            <option value="ar">العربية</option>
            <option value="ar+en">Mixed (ar + en)</option>
          </select>
        </div>

        <div className={styles.ocrField}>
          <label htmlFor="ocr-text">
            {t('ocr.queue.text', { defaultValue: 'Scanned page text (OCR input)' })}
          </label>
          <textarea
            id="ocr-text"
            value={pageText}
            onChange={(e) => setPageText(e.target.value)}
            placeholder={t('ocr.queue.placeholder', {
              defaultValue:
                'Paste the OCR output of the scanned page. Garbled tokens (e.g. "Apparat us 2", "rnedium") drop confidence and flag it for review.',
            })}
          />
        </div>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <div className={styles.ocrActions}>
          <Button variant="secondary" onClick={onClose} disabled={run.isPending}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="primary" onClick={() => void submit()} isLoading={run.isPending}>
            {t('ocr.queue.submit', { defaultValue: 'Run OCR' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
